﻿import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Heading, Button, Input, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { usePermission } from '../../hooks/usePermissions'
import { supabase } from '../../lib/supabase'
import { getAllUsers, getMultipleUsersInfo } from '../../services/wiki'
import { notificationsService, NotificationMeta } from '../../services/notifications'
import { CheckCircleIcon, XCircleIcon, XMarkIcon, SparklesIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { melhorarMensagemNotificacao, sugerirTituloNotificacaoPelaMensagem } from '../../utils/assistenteTexto'
import { BRAND_COLOR } from '../../constants'

const MENSAGEM_MAX_LENGTH = 200

const iaButtonClass =
  'shrink-0 inline-flex items-center justify-center rounded-md border border-purple-300 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/30 px-3 py-2 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'

interface HistoryUser {
  id: string
  name: string | null
  email: string | null
}

interface HistoryRecipient {
  user: HistoryUser
  read_at: string | null
  deleted_at: string | null
}

interface HistoryNotification extends NotificationMeta {
  sender?: HistoryUser
  recipients?: HistoryRecipient[]
}

function formatHistoryUserLabel(user: HistoryUser): string {
  const primary = user.name || user.email || user.id
  if (user.email && user.name) return `${primary} (${user.email})`
  return primary
}

function HistoryUserList({
  items,
  emptyLabel,
  dateKey,
}: {
  items: HistoryRecipient[]
  emptyLabel: string
  dateKey?: 'read_at' | 'deleted_at'
}) {
  if (items.length === 0) {
    return <Text className="text-gray-900 dark:text-white">{emptyLabel}</Text>
  }
  return (
    <ul className="mt-2 max-h-48 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700 rounded-md border border-gray-200 dark:border-gray-700">
      {items.map((r) => (
        <li key={r.user.id} className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
          <span>{formatHistoryUserLabel(r.user)}</span>
          {dateKey && r[dateKey] ? (
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {new Date(r[dateKey]!).toLocaleString('pt-BR')}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

interface UserGroup {
  id: string
  name: string
  display_name: string
}

interface SimpleUser {
  id: string
  name: string | null
  email: string | null
}

const ConfiguracoesNotificacoes: React.FC = () => {
  const { hasPermission: canViewValues, loading: loadingViewValues } = usePermission(
    'configuracoes_notificacoes',
    'view_values'
  )

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [iaTituloLoading, setIaTituloLoading] = useState(false)
  const [iaMensagemLoading, setIaMensagemLoading] = useState(false)
  const [iaError, setIaError] = useState<string | null>(null)
  const [groups, setGroups] = useState<UserGroup[]>([])
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [users, setUsers] = useState<SimpleUser[]>([])
  const [selectedUsers, setSelectedUsers] = useState<Record<string, boolean>>({})
  const [userQuery, setUserQuery] = useState('')
  const [groupQuery, setGroupQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null)
  const [activeTab, setActiveTab] = useState<'enviar' | 'historico' | 'relatorio'>('enviar')
  // Relatório por usuário
  const [reportLoading, setReportLoading] = useState(false)
  const [reportError, setReportError] = useState<string | null>(null)
  const [reportItems, setReportItems] = useState<Array<{ user_id: string; name: string | null; recebidas: number; lidas: number; excluidas: number; senders?: string[] }>>([])
  const [reportSearch, setReportSearch] = useState('')
  const [reportLimit, setReportLimit] = useState(100)

  const loadReport = async () => {
    setReportLoading(true)
    setReportError(null)
    try {
      const { data, error } = await supabase.rpc('notifications_stats_by_user', {
        p_limit: reportLimit,
        p_search: reportSearch || null
      })
      if (error) throw error
      setReportItems((data || []) as any)
    } catch (e: any) {
      setReportItems([])
      setReportError(e?.message || 'Erro ao carregar relatório')
    } finally {
      setReportLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab !== 'relatorio' || !canViewValues) return
    loadReport()
  }, [activeTab, reportLimit, canViewValues])
  const [history, setHistory] = useState<HistoryNotification[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)
  const [historyDateFrom, setHistoryDateFrom] = useState<string>('')
  const [historyDateTo, setHistoryDateTo] = useState<string>('')
  const [historySearch, setHistorySearch] = useState<string>('')
  const [historyRecipientSearch, setHistoryRecipientSearch] = useState<string>('')
  const [historySenderSearch, setHistorySenderSearch] = useState<string>('')
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1)
  const [historyItemsPerPage] = useState(20)
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<HistoryNotification | null>(null)
  const [isHistoryDetailOpen, setIsHistoryDetailOpen] = useState(false)
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false)

  useEffect(() => {
    const loadGroups = async () => {
      const { data, error } = await supabase.from('user_groups').select('id, name, display_name').eq('is_active', true).order('display_name')
      if (!error) setGroups((data || []) as UserGroup[])
    }
    loadGroups()
  }, [])

  useEffect(() => {
    const loadUsers = async () => {
      const list = await getAllUsers()
      let result: SimpleUser[] = list.map(u => ({ id: u.id, name: u.name, email: null }))
      // Enriquecer com emails usando função existente
      try {
        const info = await getMultipleUsersInfo(list.map(u => u.id))
        result = result.map(u => ({ ...u, email: info[u.id]?.email || null }))
      } catch {}
      setUsers(result)
    }
    loadUsers()
  }, [])

  const handleSugerirTituloIa = useCallback(async () => {
    const mensagem = body.trim()
    if (!mensagem) {
      setIaError('Preencha a mensagem antes de sugerir o título com IA.')
      return
    }
    setIaError(null)
    setIaTituloLoading(true)
    try {
      const tituloSugerido = await sugerirTituloNotificacaoPelaMensagem(mensagem)
      setTitle(tituloSugerido)
    } catch (e: unknown) {
      setIaError(e instanceof Error ? e.message : 'Erro ao sugerir título com IA.')
    } finally {
      setIaTituloLoading(false)
    }
  }, [body])

  const handleMelhorarMensagemIa = useCallback(async () => {
    const mensagem = body.trim()
    if (!mensagem) {
      setIaError('Preencha a mensagem antes de melhorá-la com IA.')
      return
    }
    setIaError(null)
    setIaMensagemLoading(true)
    try {
      const melhorada = await melhorarMensagemNotificacao(mensagem)
      setBody(melhorada.slice(0, MENSAGEM_MAX_LENGTH))
    } catch (e: unknown) {
      setIaError(e instanceof Error ? e.message : 'Erro ao melhorar mensagem com IA.')
    } finally {
      setIaMensagemLoading(false)
    }
  }, [body])

  const onToggleGroup = (id: string) => {
    setSelected(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const areAllSelected = groups.length > 0 && groups.every(g => !!selected[g.id])
  const onToggleAll = () => {
    if (areAllSelected) {
      setSelected({})
    } else {
      const next: Record<string, boolean> = {}
      for (const g of groups) next[g.id] = true
      setSelected(next)
    }
  }

  const areAllUsersSelected = users.length > 0 && users.every(u => !!selectedUsers[u.id])
  const onToggleAllUsers = () => {
    if (areAllUsersSelected) {
      setSelectedUsers({})
    } else {
      const next: Record<string, boolean> = {}
      for (const u of users) next[u.id] = true
      setSelectedUsers(next)
    }
  }

  const onSubmit = async () => {
    setMessage(null)
    setMessageType(null)
    if (!title.trim() || !body.trim()) {
      setMessage('Preencha título e mensagem')
      setMessageType('error')
      return
    }
    const userGroupIds = Object.keys(selected).filter(id => selected[id])
    const userIds = Object.keys(selectedUsers).filter(id => selectedUsers[id])
    if (userGroupIds.length === 0 && userIds.length === 0) {
      setMessage('Selecione pelo menos um grupo ou usuário')
      setMessageType('error')
      return
    }
    setLoading(true)
    const { error } = await notificationsService.sendToGroups({ title, body, userGroupIds, userIds })
    if (error) {
      setMessage(error.message)
      setMessageType('error')
    } else {
      setMessage('Notificação enviada com sucesso!')
      setMessageType('success')
      setTitle('')
      setBody('')
      setIaError(null)
      setSelected({})
      setSelectedUsers({})
      setUserQuery('')
      setGroupQuery('')
      // Auto-ocultar mensagem de sucesso após 5 segundos
      setTimeout(() => {
        setMessage(null)
        setMessageType(null)
      }, 5000)
    }
    setLoading(false)
  }

  // Carregar histórico (com filtros de data e limite)
  const loadHistory = async () => {
    setHistoryLoading(true)
    setHistoryError(null)
    try {
      let query = supabase
        .from('notifications')
        .select('id, title, body, created_by, created_at, recipients:notification_recipients(user_id, read_at, deleted_at)')
        .order('created_at', { ascending: false })
        .limit(1000)

      if (historyDateFrom) {
        query = query.gte('created_at', new Date(historyDateFrom).toISOString())
      }
      if (historyDateTo) {
        const end = new Date(historyDateTo)
        end.setHours(23, 59, 59, 999)
        query = query.lte('created_at', end.toISOString())
      }

      const { data, error } = await query
      if (error) throw error

      const rows = (data || []) as any[]
      // Enriquecer destinatários e remetentes com nome/email usando função existente
      const recipientIdSet = new Set<string>()
      const senderIdSet = new Set<string>()
      rows.forEach((n: any) => {
        if (n?.created_by) senderIdSet.add(n.created_by)
        ;(n?.recipients || []).forEach((r: any) => { if (r?.user_id) recipientIdSet.add(r.user_id) })
      })

      let usersInfo: Record<string, { name: string; email: string }> = {}
      const mergedIds: string[] = []
      recipientIdSet.forEach((id) => { mergedIds.push(id) })
      senderIdSet.forEach((id) => { mergedIds.push(id) })
      const toFetch = Array.from(new Set<string>(mergedIds))
      if (toFetch.length > 0) {
        try {
          usersInfo = await getMultipleUsersInfo(toFetch)
        } catch {}
      }

      const withUsers = rows.map((n: any) => ({
        ...n,
        sender: {
          id: n.created_by,
          name: usersInfo[n.created_by]?.name || null,
          email: usersInfo[n.created_by]?.email || null
        },
        recipients: (n?.recipients || []).map((r: any) => ({
          user: {
            id: r.user_id,
            name: usersInfo[r.user_id]?.name || null,
            email: usersInfo[r.user_id]?.email || null
          },
          read_at: r.read_at ?? null,
          deleted_at: r.deleted_at ?? null
        }))
      }))

      setHistory(withUsers as HistoryNotification[])
    } catch (e: any) {
      setHistory([])
      setHistoryError(e?.message || 'Erro ao carregar histórico')
    } finally {
      setHistoryLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab !== 'historico' || !canViewValues) return
    loadHistory()
  }, [activeTab, historyDateFrom, historyDateTo, canViewValues])

  const filteredHistory = useMemo(() => {
    const q = historySearch.trim().toLowerCase()
    const rq = historyRecipientSearch.trim().toLowerCase()
    const sq = historySenderSearch.trim().toLowerCase()
    return history.filter((n) => {
      const titleBodyMatch =
        !q ||
        (n.title?.toLowerCase().includes(q) || n.body?.toLowerCase().includes(q))
      const recipientsText = Array.isArray(n.recipients)
        ? n.recipients
            .map((r) => `${r?.user?.name || ''} ${r?.user?.email || ''}`.toLowerCase())
            .join(' ')
        : ''
      const recipientMatch = !rq || recipientsText.includes(rq)
      const senderText = `${n?.sender?.name || ''} ${n?.sender?.email || ''} ${n?.created_by || ''}`.toLowerCase()
      const senderMatch = !sq || senderText.includes(sq)
      return titleBodyMatch && recipientMatch && senderMatch
    })
  }, [history, historySearch, historyRecipientSearch, historySenderSearch])

  const historyTotalPages = Math.ceil(filteredHistory.length / historyItemsPerPage) || 1
  const historyStartIndex = (historyCurrentPage - 1) * historyItemsPerPage
  const historyEndIndex = Math.min(historyCurrentPage * historyItemsPerPage, filteredHistory.length)
  const paginatedHistory = filteredHistory.slice(historyStartIndex, historyEndIndex)

  useEffect(() => {
    setHistoryCurrentPage(1)
  }, [historySearch, historyRecipientSearch, historySenderSearch, historyDateFrom, historyDateTo])

  useEffect(() => {
    if (historyCurrentPage > historyTotalPages && historyTotalPages > 0) {
      setHistoryCurrentPage(historyTotalPages)
    }
  }, [historyCurrentPage, historyTotalPages])

  const handleHistoryPreviousPage = () => {
    if (historyCurrentPage > 1) setHistoryCurrentPage((p) => p - 1)
  }

  const handleHistoryNextPage = () => {
    if (historyCurrentPage < historyTotalPages) setHistoryCurrentPage((p) => p + 1)
  }

  const handleHistoryPageChange = (page: number) => {
    if (page >= 1 && page <= historyTotalPages) setHistoryCurrentPage(page)
  }

  const openHistoryDetail = async (item: HistoryNotification) => {
    setSelectedHistoryItem(item)
    setIsHistoryDetailOpen(true)
    setHistoryDetailLoading(true)
    try {
      const { data, error } = await supabase
        .from('notification_recipients')
        .select('user_id, read_at, deleted_at')
        .eq('notification_id', item.id)

      if (error) throw error

      const rows = (data || []) as Array<{ user_id: string; read_at: string | null; deleted_at: string | null }>
      const userIds = Array.from(new Set(rows.map((r) => r.user_id)))
      let usersInfo: Record<string, { name: string; email: string }> = {}
      if (userIds.length > 0) {
        try {
          usersInfo = await getMultipleUsersInfo(userIds)
        } catch {}
      }

      const recipients: HistoryRecipient[] = rows.map((r) => ({
        user: {
          id: r.user_id,
          name: usersInfo[r.user_id]?.name || null,
          email: usersInfo[r.user_id]?.email || null
        },
        read_at: r.read_at,
        deleted_at: r.deleted_at
      }))

      setSelectedHistoryItem({ ...item, recipients })
    } catch {
      // Mantém dados da listagem se a busca detalhada falhar
    } finally {
      setHistoryDetailLoading(false)
    }
  }

  const closeHistoryDetail = () => {
    setSelectedHistoryItem(null)
    setIsHistoryDetailOpen(false)
    setHistoryDetailLoading(false)
  }

  useEffect(() => {
    if (loadingViewValues) return
    if (!canViewValues && (activeTab === 'historico' || activeTab === 'relatorio')) {
      setActiveTab('enviar')
    }
  }, [loadingViewValues, canViewValues, activeTab])

  return (
    <>
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard 
          moduleName="configuracoes_notificacoes" 
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar este recurso.
                </Text>
              </div>
            </div>
          }
        >
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                Notificações
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                {canViewValues && !loadingViewValues
                  ? 'Envie notificações in-app e visualize o histórico de envios.'
                  : 'Envie notificações in-app aos grupos e usuários selecionados.'}
              </Text>
            </div>

            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                <button
                  className={`${activeTab === 'enviar' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'} whitespace-nowrap border-b-2 py-2 px-1 text-sm font-medium`}
                  onClick={() => setActiveTab('enviar')}
                >
                  Enviar
                </button>
                {canViewValues && !loadingViewValues && (
                  <>
                    <button
                      className={`${activeTab === 'historico' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'} whitespace-nowrap border-b-2 py-2 px-1 text-sm font-medium`}
                      onClick={() => setActiveTab('historico')}
                    >
                      Histórico
                    </button>
                    <button
                      className={`${activeTab === 'relatorio' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'} whitespace-nowrap border-b-2 py-2 px-1 text-sm font-medium`}
                      onClick={() => setActiveTab('relatorio')}
                    >
                      Relatório
                    </button>
                  </>
                )}
              </nav>
            </div>

            {activeTab === 'enviar' && (
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="grid gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Título</label>
                <div className="flex gap-2">
                  <Input
                    value={title}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
                    placeholder="Título da notificação"
                    className="flex-1 min-w-0"
                  />
                  <button
                    type="button"
                    onClick={() => void handleSugerirTituloIa()}
                    disabled={iaTituloLoading || iaMensagemLoading || !body.trim()}
                    title="Sugerir título chamativo com emojis (IA) a partir da mensagem"
                    aria-label="Sugerir título chamativo com emojis (IA) a partir da mensagem"
                    className={iaButtonClass}
                  >
                    {iaTituloLoading ? (
                      <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden />
                    ) : (
                      <SparklesIcon className="h-5 w-5" aria-hidden />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Mensagem</label>
                  <span className={`text-xs ${body.length > MENSAGEM_MAX_LENGTH ? 'text-red-600 dark:text-red-400' : body.length > 180 ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-500 dark:text-gray-400'}`}>
                    {body.length}/{MENSAGEM_MAX_LENGTH}
                  </span>
                </div>
                <div className="flex gap-2 items-start">
                  <textarea
                    className="flex-1 min-w-0 h-36 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                    value={body}
                    onChange={(e) => {
                      const newValue = e.target.value.slice(0, MENSAGEM_MAX_LENGTH)
                      setBody(newValue)
                    }}
                    maxLength={MENSAGEM_MAX_LENGTH}
                    placeholder="Escreva a mensagem..."
                  />
                  <button
                    type="button"
                    onClick={() => void handleMelhorarMensagemIa()}
                    disabled={iaMensagemLoading || iaTituloLoading || !body.trim()}
                    title="Melhorar mensagem com IA"
                    aria-label="Melhorar mensagem com IA"
                    className={iaButtonClass}
                  >
                    {iaMensagemLoading ? (
                      <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden />
                    ) : (
                      <SparklesIcon className="h-5 w-5" aria-hidden />
                    )}
                  </button>
                </div>
                {iaError && (
                  <Text size="xs" className="text-red-600 dark:text-red-400 mt-1">
                    {iaError}
                  </Text>
                )}
              </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Grupos de usuário</label>
                    <div className="mb-2">
                      <Input value={groupQuery} onChange={(e: any) => setGroupQuery(e.target.value)} placeholder="Buscar por nome do grupo" />
                    </div>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Lista de grupos</span>
                        <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                          <input type="checkbox" checked={areAllSelected} onChange={onToggleAll} />
                          <span>Selecionar todos</span>
                        </label>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        <ul role="list" className="divide-y divide-gray-200 dark:divide-gray-700">
                          {groups
                            .filter((g) => {
                              if (!groupQuery.trim()) return true
                              const q = groupQuery.toLowerCase()
                              return (
                                (g.display_name || '').toLowerCase().includes(q) ||
                                (g.name || '').toLowerCase().includes(q)
                              )
                            })
                            .map((g) => (
                              <li key={g.id} className="px-3 py-2">
                                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                  <input
                                    type="checkbox"
                                    checked={!!selected[g.id]}
                                    onChange={() => onToggleGroup(g.id)}
                                  />
                                  <span className="truncate">{g.display_name}</span>
                                </label>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Usuários específicos (opcional)</label>
                    <div className="mb-2">
                      <Input value={userQuery} onChange={(e: any) => setUserQuery(e.target.value)} placeholder="Buscar por nome ou email" />
                    </div>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Lista de usuários</span>
                        <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                          <input type="checkbox" checked={areAllUsersSelected} onChange={onToggleAllUsers} />
                          <span>Selecionar todos</span>
                        </label>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        <ul role="list" className="divide-y divide-gray-200 dark:divide-gray-700">
                          {users
                            .filter(u => {
                              if (!userQuery.trim()) return true
                              const q = userQuery.toLowerCase()
                              return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)
                            })
                            .map(u => (
                              <li key={u.id} className="px-3 py-2">
                                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                  <input
                                    type="checkbox"
                                    checked={!!selectedUsers[u.id]}
                                    onChange={() => setSelectedUsers(prev => ({ ...prev, [u.id]: !prev[u.id] }))}
                                  />
                                  <span className="truncate">
                                    {u.name || u.email || u.id}
                                    {u.email && u.name ? <span className="text-gray-400"> ({u.email})</span> : null}
                                  </span>
                                </label>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>

                    {message && (
                      <div className={`mt-4 p-4 rounded-lg border ${
                        messageType === 'success'
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      }`}>
                        <div className="flex items-start gap-3">
                          {messageType === 'success' ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                          ) : (
                            <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <Text 
                              size="sm" 
                              className={messageType === 'success'
                                ? 'text-green-800 dark:text-green-300 font-medium'
                                : 'text-red-800 dark:text-red-300 font-medium'
                              }
                            >
                              {message}
                            </Text>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="mt-4 flex justify-end">
                      <PermissionGuard moduleName="configuracoes_notificacoes" permissionType="insert" fallback={null}>
                        <Button onClick={onSubmit} disabled={loading} className="bg-primary-600 hover:bg-primary-700 text-white">
                          {loading ? 'Enviando...' : 'Enviar notificação'}
                        </Button>
                      </PermissionGuard>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {canViewValues && !loadingViewValues && activeTab === 'historico' && (
              <div className="mt-6 bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Últimas notificações</span>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={loadHistory} className="text-gray-600 dark:text-gray-300">Atualizar</Button>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Título/Corpo</label>
                      <Input value={historySearch} onChange={(e: any) => setHistorySearch(e.target.value)} placeholder="Buscar..." />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Destinatário</label>
                      <Input value={historyRecipientSearch} onChange={(e: any) => setHistoryRecipientSearch(e.target.value)} placeholder="Nome ou email" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Remetente</label>
                      <Input value={historySenderSearch} onChange={(e: any) => setHistorySenderSearch(e.target.value)} placeholder="Nome ou email" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">De</label>
                      <Input type="date" value={historyDateFrom} onChange={(e: any) => setHistoryDateFrom(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Até</label>
                      <Input type="date" value={historyDateTo} onChange={(e: any) => setHistoryDateTo(e.target.value)} />
                    </div>
                  </div>
                </div>
              {historyLoading && (
                <div className="p-4 text-sm text-gray-600 dark:text-gray-300">Carregando...</div>
              )}
                {historyError && (
                  <div className="p-4 text-sm text-red-700 dark:text-red-300">{historyError}</div>
                )}
                {!historyLoading && !historyError && (
                  <>
                    <ul role="list" className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredHistory.length === 0 && (
                        <li className="p-4 text-sm text-gray-500 dark:text-gray-400">Nenhuma notificação encontrada.</li>
                      )}
                      {paginatedHistory.map((n) => {
                        const recipients = Array.isArray(n.recipients)
                          ? n.recipients.map((r) => r.user).filter(Boolean)
                          : []
                        const shown = recipients.slice(0, 3)
                        const remaining = recipients.length - shown.length
                        return (
                          <li
                            key={n.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => void openHistoryDetail(n)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                void openHistoryDetail(n)
                              }
                            }}
                            className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                        <div className="pr-4">
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{n.title}</div>
                          <div className="mt-1 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">{n.body}</div>
                          <p className="mt-1 text-xs text-primary-600 dark:text-primary-400">Clique para ver a mensagem completa</p>
                          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            <span className="font-medium">Enviado por:</span>{' '}
                            {n?.sender?.name || n?.sender?.email || n?.created_by || '—'}
                          </div>
                                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                  <span className="font-medium">Enviado para:</span>{' '}
                                  {shown.length === 0 ? (
                                    <span>Ninguém</span>
                                  ) : (
                                    <>
                                      {shown.map((u, idx) => (
                                        <span key={u.id} className="mr-2 inline-block">
                                          {u.name || u.email || u.id}{idx < shown.length - 1 ? ',' : ''}
                                        </span>
                                      ))}
                                      {remaining > 0 && (
                                        <span className="text-gray-400">+{remaining} mais</span>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap shrink-0">
                                {new Date(n.created_at).toLocaleString('pt-BR')}
                              </div>
                            </div>
                          </li>
                        )
                      })}
                    </ul>

                    {filteredHistory.length > 0 && (
                      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <Text size="sm" className="text-gray-700 dark:text-gray-300">
                            Mostrando {historyStartIndex + 1} a {historyEndIndex} de {filteredHistory.length} registros
                          </Text>

                          {historyTotalPages > 1 && (
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={handleHistoryPreviousPage}
                                disabled={historyCurrentPage === 1}
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                              >
                                Anterior
                              </button>

                              <div className="flex space-x-1">
                                {Array.from({ length: Math.min(5, historyTotalPages) }, (_, i) => {
                                  let pageNumber: number
                                  if (historyTotalPages <= 5) {
                                    pageNumber = i + 1
                                  } else if (historyCurrentPage <= 3) {
                                    pageNumber = i + 1
                                  } else if (historyCurrentPage >= historyTotalPages - 2) {
                                    pageNumber = historyTotalPages - 4 + i
                                  } else {
                                    pageNumber = historyCurrentPage - 2 + i
                                  }

                                  return (
                                    <button
                                      key={pageNumber}
                                      type="button"
                                      onClick={() => handleHistoryPageChange(pageNumber)}
                                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                                        historyCurrentPage === pageNumber
                                          ? 'bg-primary-600 text-white'
                                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                      }`}
                                    >
                                      {pageNumber}
                                    </button>
                                  )
                                })}
                              </div>

                              <button
                                type="button"
                                onClick={handleHistoryNextPage}
                                disabled={historyCurrentPage === historyTotalPages}
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                              >
                                Próximo
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {canViewValues && !loadingViewValues && activeTab === 'relatorio' && (
              <div className="mt-6 bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-end">
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={loadReport} className="text-gray-600 dark:text-gray-300">Atualizar</Button>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Buscar usuário</label>
                      <Input value={reportSearch} onChange={(e: any) => setReportSearch(e.target.value)} placeholder="Nome ou email" />
                    </div>
              <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Limite</label>
                      <select
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 p-2 text-sm"
                        value={reportLimit}
                        onChange={(e) => setReportLimit(Number(e.target.value))}
                      >
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                      </select>
                    </div>
                  </div>
                </div>
                {reportLoading && (
                  <div className="p-4 text-sm text-gray-600 dark:text-gray-300">Carregando...</div>
                )}
                {reportError && (
                  <div className="p-4 text-sm text-red-700 dark:text-red-300">{reportError}</div>
                )}
                {!reportLoading && !reportError && (
                  <div className="overflow-x-auto">
                    <div className="min-w-full">
                      <div className="grid grid-cols-4 px-6 py-2 text-xs font-medium uppercase tracking-wider bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400">
                        <div>Usuário</div>
                        <div>Recebidas</div>
                        <div>Lidas</div>
                        <div>Excluídas</div>
                      </div>
                      <ul role="list" className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                        {reportItems
                          .filter((u) => {
                            const q = reportSearch.trim().toLowerCase()
                            if (!q) return true
                            return (u.name || '').toLowerCase().includes(q)
                          })
                          .map((u) => (
                            <li key={u.user_id} className="grid grid-cols-4 px-6 py-3">
                              <div className="whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{u.name || u.user_id}</div>
                              <div className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{u.recebidas}</div>
                              <div className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{u.lidas}</div>
                              <div className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{u.excluidas}</div>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </PermissionGuard>
      </div>

      {isHistoryDetailOpen && selectedHistoryItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {selectedHistoryItem.title}
              </Heading>
              <Button variant="ghost" size="sm" onClick={closeHistoryDetail} className="p-2">
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                  Data/Hora
                </Text>
                <Text className="text-gray-900 dark:text-white">
                  {new Date(selectedHistoryItem.created_at).toLocaleString('pt-BR')}
                </Text>
              </div>

              <div>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                  Enviado por
                </Text>
                <Text className="text-gray-900 dark:text-white">
                  {selectedHistoryItem.sender?.name ||
                    selectedHistoryItem.sender?.email ||
                    selectedHistoryItem.created_by ||
                    '—'}
                </Text>
              </div>

              <div>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                  Mensagem
                </Text>
                <Text className="text-gray-900 dark:text-white whitespace-pre-wrap break-words">
                  {selectedHistoryItem.body}
                </Text>
              </div>

              {historyDetailLoading ? (
                <Text size="sm" className="text-gray-500 dark:text-gray-400">
                  Carregando status dos destinatários...
                </Text>
              ) : (
                <>
                  <div>
                    <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                      Destinatários ({selectedHistoryItem.recipients?.length ?? 0})
                    </Text>
                    <HistoryUserList
                      items={selectedHistoryItem.recipients ?? []}
                      emptyLabel="Ninguém"
                    />
                  </div>

                  <div>
                    <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                      Visualizaram (
                      {selectedHistoryItem.recipients?.filter((r) => r.read_at).length ?? 0})
                    </Text>
                    <HistoryUserList
                      items={(selectedHistoryItem.recipients ?? []).filter((r) => r.read_at)}
                      emptyLabel="Nenhum usuário visualizou ainda"
                      dateKey="read_at"
                    />
                  </div>

                  <div>
                    <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                      Excluíram (
                      {selectedHistoryItem.recipients?.filter((r) => r.deleted_at).length ?? 0})
                    </Text>
                    <HistoryUserList
                      items={(selectedHistoryItem.recipients ?? []).filter((r) => r.deleted_at)}
                      emptyLabel="Nenhum usuário excluiu a mensagem"
                      dateKey="deleted_at"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <Button onClick={closeHistoryDetail} style={{ backgroundColor: BRAND_COLOR }} className="w-full">
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ConfiguracoesNotificacoes


